import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import { TrendLineChart } from '../../../../../TrendLineChart';
import { Fragment } from 'react';
import MetricValue from '../MetricValue';
import { useMetricSidebarStore } from '../../../../../hooks/useMetricSidebarStore';
import { useDashboardStore } from '../../../../../hooks/useDashboardStore';
import {
  PR_CHART_KEYS,
  ISSUE_CHART_KEYS,
  MEETING_CHARTS,
  WORK_BREAKDOWN_CHARTS,
  DATA_POINT_KEYS,
  WORK_BREAKDOWN_STATS_CHARTS,
  COMMIT_CHART_KEYS,
  DEPLOY_CHART_KEYS,
  CFR_CHART_KEYS,
  CONTRIBUTOR_CHART_KEYS,
} from '../../../../../shared/utils';
import {
  getWorkBreakdownType,
  TOTAL_COUNT_METRICS,
} from '../../../utils/contributorList.utils';
import { getMetricLabel } from '../../../utils/contributorList.utils';
import { ProgressBar } from '../../../../../ProgressBar';
import type { MetricRowData, MetricTooltip } from '../../../types';

interface TrendCellProps {
  showContributorPercentiles?: boolean;
  data: MetricRowData[];
  title: string;
  compareTo: string;
  showRightBorder?: boolean;
  moveToolTip?: boolean;
  metric: string;
  rowHeight?: string;
  metricList: MetricTooltip[];
  author?: string;
  team?: string;
  startDate?: string;
  endDate?: string;
  componentOrigin?: string;
  /** Table body cell inside CSS Grid: no trailing Divider, stretch height, bottom border. */
  gridCell?: boolean;
}

// This component is used as a cell in MetricCol component of contributor table and also used in team table directly

const TrendCell = ({
  showContributorPercentiles,
  data,
  title,
  compareTo,
  showRightBorder,
  moveToolTip,
  metric,
  rowHeight,
  metricList,
  author,
  team,
  startDate,
  endDate,
  componentOrigin,
  gridCell = false,
}: TrendCellProps) => {
  const { selected, selectedTeam } = useDashboardStore();
  const { openSidebar } = useMetricSidebarStore();
  const viewDetails =
    PR_CHART_KEYS.includes(metric) ||
    ISSUE_CHART_KEYS.includes(metric) ||
    WORK_BREAKDOWN_CHARTS.includes(metric) ||
    WORK_BREAKDOWN_STATS_CHARTS.includes(metric) ||
    MEETING_CHARTS.includes(metric) ||
    COMMIT_CHART_KEYS.includes(metric) ||
    DEPLOY_CHART_KEYS.includes(metric) ||
    CFR_CHART_KEYS.includes(metric) ||
    (DATA_POINT_KEYS.includes(metric) && metric !== 'AI_ADOPTION') ||
    CONTRIBUTOR_CHART_KEYS.includes(metric);
  return (
    <Fragment>
      <Flex
        direction="column"
        align="flex-start"
        justify={'start'}
        minH={rowHeight || '110px'}
        h={gridCell ? '100%' : rowHeight || '110px'}
        borderRight={
          gridCell
            ? undefined
            : showRightBorder
            ? '1px solid #E0E0E0'
            : undefined
        }
        borderLeft={gridCell ? undefined : '1px solid #E0E0E0'}
        borderBottom={gridCell ? '1px solid #E0E0E0' : undefined}
        p={3}
        pb={gridCell ? 3 : 0}
        bg="white"
        gap={data.length > 1 ? 3 : 0}
      >
        {data.map((item: MetricRowData) => {
          const metricInfo =
            metricList.find(
              (metricItem: MetricTooltip) => metricItem?.name === item?.metric
            ) || null;

          const handleViewDetailsClick = () => {
            openSidebar({
              isOpen: true,
              metric: WORK_BREAKDOWN_STATS_CHARTS.includes(item?.metric)
                ? 'WORK_BREAKDOWN'
                : item?.metric,
              title: title,
              data: item,
              startDate: startDate || selected.startDate,
              endDate: endDate || selected.endDate,
              sprintId: selected.sprintId,
              author: author,
              teamId: team ?? selectedTeam.value,
              view: ['ACTIVE_WORKLOAD', 'TOTAL_WORKLOAD'].includes(metric)
                ? metric
                : '',
              type: WORK_BREAKDOWN_STATS_CHARTS.includes(item?.metric)
                ? getWorkBreakdownType(item?.metric)
                : '',
              subTitle: [
                'ACTIVE_WORKLOAD',
                'TOTAL_WORKLOAD',
                'GIT_ACTIVITY',
                'AI_ADOPTION',
                'ISSUE_ACTIVITY',
                'DORA_ACTIVITY',
              ].includes(metric)
                ? getMetricLabel(item?.metric)
                : '',
            });
          };

          return (
            <Flex
              align="flex-start"
              gap={3}
              w={'full'}
              px={1}
              mb={'2px'}
              // direction={'column'}
            >
              <MetricValue
                value={
                  TOTAL_COUNT_METRICS.includes(metric) &&
                  componentOrigin !== 'team-table' //Added this condition to avoid showing the total count in the team table
                    ? item?.totalCount ?? 0
                    : Number(item?.count) || 0
                }
                unit={item?.unit as 'Days' | 'Minutes' | 'Count'}
                metric={metricInfo ?? undefined}
                subMetric={item?.metric}
                showLable={data?.length > 1}
                percentage={item?.percentage ?? undefined}
                improved={item?.improved}
                viewDetails={viewDetails}
                handleViewDetailsClick={handleViewDetailsClick}
              />
              {/* <Text fontSize={'xs'}>{author}</Text> */}
            </Flex>
          );
        })}
        {![
          'OVERDUE_TICKETS',
          'BLOCKED_TICKETS',
          'ACTIVE_WORKLOAD',
          'TOTAL_WORKLOAD',
          'TIME_TO_ZERO',
        ].includes(metric) &&
          compareTo === 'pastPeriod' &&
          data.length === 1 && (
            <TrendLineChart
              data={JSON.parse(data?.[0]?.data)}
              unit={data?.[0]?.unit}
              color={data?.[0]?.improved ? '#1DA44A' : '#E65100'}
              moreLeft
              toolTipCord={moveToolTip ? { x: undefined, y: -90 } : undefined}
              metric={metric}
              title={title}
              author={author}
              teamId={team}
              viewDetails={viewDetails}
            />
          )}
        {![
          'OVERDUE_TICKETS',
          'BLOCKED_TICKETS',
          'ACTIVE_WORKLOAD',
          'TOTAL_WORKLOAD',
          'TIME_TO_ZERO',
        ].includes(metric) &&
          showContributorPercentiles && (
            <Box height="70px" width="150px" display="grid" placeItems="center">
              {data?.[0]?.percentileData?.percentChange !== null &&
              data?.[0]?.percentileData?.percentChange !== undefined ? (
                <ProgressBar
                  color={
                    data?.[0]?.percentileData?.improved ? '#1DA44A' : '#E65100'
                  }
                  contAvg={data?.[0]?.percentileData?.percentChange}
                />
              ) : (
                <Flex align="center" justify={'center'} gap={1}>
                  <Text
                    fontSize="sm"
                    fontFamily="heading"
                    fontWeight="semibold"
                    color="text.secondary3"
                  >
                    {'_'}
                  </Text>
                </Flex>
              )}
            </Box>
          )}
      </Flex>
      {!gridCell && <Divider />}
    </Fragment>
  );
};

export default TrendCell;
